import React, { useEffect, useMemo, useRef, useState } from "react";
import { Circle, GoogleMap, OverlayView, useJsApiLoader } from "@react-google-maps/api";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import HouseLocationService, {
  HouseLocationItem,
  HouseServiceStatus,
} from "../../services/HouseLocationService";
import CookLiveLocationService, {
  RecentCookLocationResponse,
} from "../../services/CookLiveLocationService";
import AssignCookOnOneMealDialog from "../Common/AssignCookOnOneMealDialog";
import CityService from "../../services/CityService";
import { PaymentStatusEnum } from "../../enums/PaymentStatusEnum";
import { TimeEnum } from "../../enums/TimeEnum";

type HouseLocationData = {
  id: number;
  houseId: number;
  status: HouseServiceStatus;
  lat: number;
  lng: number;
  scheduledTime: string | null;
  cookId: number | null;
  cookName: string | null;
  customerName: string | null;
  city: string | null;
  area: string | null;
  raw: HouseLocationItem;
};

type CookLocation = {
  cookId: number;
  name: string;
  lat: number;
  lng: number;
  isMoving: boolean;
  lastUpdated: string;
  rawLastUpdated: string | null;
  ongoingVisitId: number | null;
  ongoingVisitTime: string | null;
  ongoingHouseId: number | null;
  nextVisitId: number | null;
  nextVisitTime: string | null;
  nextHouseId: number | null;
};

type CookWithDistance = CookLocation & { distanceKm: number; withinRadius: boolean };
type CookWithOffset = CookLocation & { offsetX: number; offsetY: number };

const RADIUS_KM = 5;
const RADIUS_M = RADIUS_KM * 1000;
const SIDEBAR_WIDTH = 320;
const DEFAULT_CENTER = { lat: 28.44918, lng: 77.030228 };
const NEARBY_THRESHOLD = 0.00012;

const cityService = new CityService();

const STATUS_COLORS: Record<HouseServiceStatus, string> = {
  PENDING: "#f59e0b",
  COOK_ALLOCATED: "#3b82f6",
  COOK_CONFIRMED: "#10b981",
  ONGOING: "#8b5cf6",
  CANCELLED: "#ef4444",
  FULFILLED: "#6b7280",
  RESCHEDULED: "#f97316",
};

const STATUS_OPTIONS: HouseServiceStatus[] = [
  "PENDING", "COOK_ALLOCATED", "COOK_CONFIRMED",
  "ONGOING", "CANCELLED", "FULFILLED", "RESCHEDULED",
];

const STATUS_LABEL: Record<HouseServiceStatus, string> = {
  PENDING: "Pending",
  COOK_ALLOCATED: "Cook Allocated",
  COOK_CONFIRMED: "Cook Confirmed",
  ONGOING: "Ongoing",
  CANCELLED: "Cancelled",
  FULFILLED: "Fulfilled",
  RESCHEDULED: "Rescheduled",
};

const haversineKm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const fmtAge = (isoDate: string | null): string => {
  if (!isoDate) return "N/A";
  const m = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} day(s) ago`;
};

const fmtDist = (km: number): string =>
  km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;

const parseTimeToMinutes = (timeStr: string | null): number => {
  if (!timeStr) return 9999;
  const parts = timeStr.trim().split(" ");
  const period = parts[1] ?? "AM";
  const [hStr, mStr] = (parts[0] ?? "0:0").split(":");
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (period === "PM" && h !== 12) h += 12;
  if (period === "AM" && h === 12) h = 0;
  return h * 60 + m;
};

const isCookFree = (cook: CookLocation): boolean => {
  if (cook.ongoingVisitId) return false;
  if (!cook.nextVisitId || !cook.nextVisitTime) return true;
  try {
    const visitMin = parseTimeToMinutes(cook.nextVisitTime);
    const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
    return (visitMin - nowMin) > 2 * 60;
  } catch {
    return false;
  }
};

const getCookStatus = (cook: CookLocation): string => {
  if (cook.isMoving) return "Travelling";
  if (cook.ongoingVisitId) return "Ongoing";
  if (cook.nextVisitId) return `Next at ${cook.nextVisitTime}`;
  return "Free";
};

const getCookStatusStyle = (cook: CookLocation): { bg: string; color: string } => {
  if (cook.isMoving) return { bg: "#dcfce7", color: "#16a34a" };
  if (cook.ongoingVisitId) return { bg: "#dbeafe", color: "#1d4ed8" };
  if (cook.nextVisitId) return { bg: "#fef3c7", color: "#92400e" };
  return { bg: "#f3f4f6", color: "#6b7280" };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractLatLng(item: any): { lat: number; lng: number } | null {
  const ok = (la: unknown, lo: unknown) => {
    const lat = Number(la), lng = Number(lo);
    return !isNaN(lat) && !isNaN(lng) && Math.abs(lat) > 0.1 && Math.abs(lng) > 0.1
      ? { lat, lng } : null;
  };
  if (!item) return null;
  if (item.houseCoordinates) {
    const r = ok(item.houseCoordinates.lat, item.houseCoordinates.long)
           || ok(item.houseCoordinates.lat, item.houseCoordinates.lng);
    if (r) return r;
  }
  if (item.location?.coordinates?.length >= 2) {
    const r = ok(item.location.coordinates[1], item.location.coordinates[0]);
    if (r) return r;
  }
  if (item.lat != null || item.long != null) {
    const r = ok(item.lat, item.lng) || ok(item.lat, item.long);
    if (r) return r;
  }
  if (item.latitude != null) {
    const r = ok(item.latitude, item.longitude);
    if (r) return r;
  }
  if (item.house) { const r = extractLatLng(item.house); if (r) return r; }
  if (item.address) { const r = extractLatLng(item.address); if (r) return r; }
  if (item.type === "Point" && item.coordinates?.length >= 2) {
    const r = ok(item.coordinates[1], item.coordinates[0]);
    if (r) return r;
  }
  if (item.coordinates && typeof item.coordinates === "object" && !Array.isArray(item.coordinates)) {
    const r = ok(item.coordinates.lat, item.coordinates.lng)
           || ok(item.coordinates.lat, item.coordinates.long);
    if (r) return r;
  }
  return null;
}

const mapHouseItem = (item: HouseLocationItem): HouseLocationData | null => {
  const coords = extractLatLng(item);
  if (!coords) { console.warn("[HouseLocation] no coords:", item); return null; }
  return {
    id: item.id ?? item.omsId ?? 0,
    houseId: item.houseId ?? item.house_id ?? item.id ?? 0,
    status: (item.status as HouseServiceStatus) ?? "PENDING",
    lat: coords.lat, lng: coords.lng,
    scheduledTime: item.scheduledTime ?? item.time ?? item.visitTime ?? null,
    cookId: item.cookId ?? null,
    cookName: item.cookName ?? null,
    customerName: item.customerName ?? item.userName ?? null,
    city: item.city ?? item.house?.city ?? null,
    area: item.area ?? item.house?.area ?? null,
    raw: item,
  };
};

const mapCookItem = (cook: RecentCookLocationResponse): CookLocation | null => {
  if (!cook.currentLocation?.coordinates || cook.currentLocation.coordinates.length < 2) return null;
  const [lng, lat] = cook.currentLocation.coordinates;
  if (Math.abs(lat) < 0.1 || Math.abs(lng) < 0.1) return null;
  return {
    cookId: cook.cookId,
    name: cook.cookName,
    lat, lng,
    isMoving: cook.isCookMoving,
    lastUpdated: fmtAge(cook.currentLocationCreatedAt),
    rawLastUpdated: cook.currentLocationCreatedAt,
    ongoingVisitId: cook.ongoingVisit?.id ?? null,
    ongoingVisitTime: cook.ongoingVisit?.time ?? null,
    ongoingHouseId: cook.ongoingVisit?.houseId ?? null,
    nextVisitId: cook.nextVisit?.id ?? null,
    nextVisitTime: cook.nextVisit?.time ?? null,
    nextHouseId: cook.nextVisit?.houseId ?? null,
  };
};

const withOffsets = (cooks: CookLocation[]): CookWithOffset[] => {
  const groups = new Map<string, CookLocation[]>();
  cooks.forEach((c) => {
    const key = `${Math.round(c.lat / NEARBY_THRESHOLD)}:${Math.round(c.lng / NEARBY_THRESHOLD)}`;
    const g = groups.get(key) || [];
    g.push(c);
    groups.set(key, g);
  });
  return Array.from(groups.values()).flatMap((grp) => {
    const s = [...grp].sort((a, b) => a.cookId - b.cookId);
    return s.map((cook, i) => {
      if (i === 0 || s.length === 1) return { ...cook, offsetX: 0, offsetY: 0 };
      const ring = Math.floor((i - 1) / 8) + 1;
      const angle = (((i - 1) % 8) * 45 * Math.PI) / 180;
      const r = 28 + (ring - 1) * 20;
      return { ...cook, offsetX: Math.cos(angle) * r, offsetY: Math.sin(angle) * r };
    });
  });
};

const HousePin = ({ color, selected }: { color: string; selected: boolean }) => (
  <svg
    width={selected ? 34 : 26}
    height={selected ? 46 : 36}
    viewBox="0 0 30 42"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block" }}
  >
    <path
      d="M15 0C6.72 0 0 6.72 0 15c0 11.25 15 27 15 27S30 26.25 30 15C30 6.72 23.28 0 15 0z"
      fill={color}
      stroke={selected ? "#1a1a1a" : "white"}
      strokeWidth={selected ? "2.5" : "1.5"}
    />
    <path d="M15 7 L7 14 L23 14 Z" fill="white" />
    <rect x="9" y="14" width="12" height="8" fill="white" />
    <rect x="13" y="17" width="4" height="5" fill={color} />
  </svg>
);

const HouseTooltip = ({
  house,
  freeCooksNearby,
  transform,
  onEnter,
  onLeave,
  onNavigate,
}: {
  house: HouseLocationData;
  freeCooksNearby: number;
  transform: string;
  onEnter: () => void;
  onLeave: () => void;
  onNavigate: (id: number) => void;
}) => (
  <Box
    onMouseEnter={onEnter}
    onMouseLeave={onLeave}
    sx={{
      transform,
      minWidth: 200,
      px: 1.5,
      py: 1.25,
      borderRadius: "10px",
      bgcolor: "rgba(255,255,255,0.98)",
      boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
      border: "1px solid rgba(0,0,0,0.08)",
      pointerEvents: "auto",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
      <Typography fontWeight={700} fontSize="0.9rem">House #{house.houseId}</Typography>
      <Typography
        variant="caption" fontWeight={600}
        sx={{ px: 0.75, py: 0.15, borderRadius: "4px", bgcolor: STATUS_COLORS[house.status] + "22", color: STATUS_COLORS[house.status] }}
      >
        {STATUS_LABEL[house.status]}
      </Typography>
    </Box>
    {(house.city || house.area) && (
      <Typography variant="body2" color="text.secondary" fontSize="0.76rem" sx={{ mb: 0.3 }}>
        {[house.area, house.city].filter(Boolean).join(", ")}
      </Typography>
    )}
    {house.customerName && (
      <Typography variant="body2" color="text.secondary" fontSize="0.78rem" sx={{ mb: 0.3 }}>
        {house.customerName}
      </Typography>
    )}
    {house.scheduledTime && (
      <Typography variant="body2" fontSize="0.78rem" sx={{ mb: 0.3 }}>
        Time: <Box component="span" fontWeight={600}>{house.scheduledTime}</Box>
      </Typography>
    )}
    {house.cookName && (
      <Typography variant="body2" fontSize="0.78rem" sx={{ mb: 0.3 }}>
        Cook: <Box component="span" fontWeight={600}>{house.cookName}</Box>
      </Typography>
    )}
    <Box sx={{ mt: 0.75, px: 1, py: 0.4, borderRadius: "6px", bgcolor: "#e0e7ff", display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography variant="caption" fontWeight={700} color="#4338ca">
        {freeCooksNearby} free cook{freeCooksNearby !== 1 ? "s" : ""} within {RADIUS_KM} km
      </Typography>
    </Box>
    <Box
      onClick={() => onNavigate(house.id)}
      sx={{ mt: 0.5, fontSize: "0.68rem", color: "#2563EB", fontWeight: 600, cursor: "pointer", width: "fit-content", "&:hover": { textDecoration: "underline" } }}
    >
      OMS #{house.id} →
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.66rem", display: "block", mt: 0.25 }}>
      Click pin to show {RADIUS_KM}km radius
    </Typography>
  </Box>
);

const CookTooltip = ({
  cook,
  selectedHouse,
  transform,
  onEnter,
  onLeave,
  onNavigate,
  onCookProfile,
  onAssign,
  onClose,
}: {
  cook: CookLocation;
  selectedHouse: HouseLocationData | null;
  transform: string;
  onEnter: () => void;
  onLeave: () => void;
  onNavigate: (path: string) => void;
  onCookProfile?: (cookId: number) => void;
  onAssign?: () => void;
  onClose?: () => void;
}) => {
  const statusStyle = getCookStatusStyle(cook);
  const dist = selectedHouse
    ? haversineKm(selectedHouse.lat, selectedHouse.lng, cook.lat, cook.lng)
    : null;
  const within = dist !== null && dist <= RADIUS_KM;

  return (
    <Box
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      sx={{
        position: "relative",
        transform,
        minWidth: 200,
        px: 1.5, py: 1.25, borderRadius: "10px",
        bgcolor: "rgba(255,255,255,0.98)",
        boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
        border: onAssign ? "1.5px solid #6366f1" : "1px solid rgba(0,0,0,0.08)",
        pointerEvents: "auto",
      }}
    >
      {onClose && (
        <Box
          onClick={onClose}
          sx={{ position: "absolute", top: 6, right: 8, cursor: "pointer", fontSize: "0.75rem", color: "#94a3b8", fontWeight: 700, lineHeight: 1, "&:hover": { color: "#1e293b" } }}
        >
          ✕
        </Box>
      )}
      <Typography fontWeight={700} fontSize="0.9rem" sx={{ mb: 0.25 }}>{cook.name}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: "0.72rem", px: 0.75, py: 0.2, borderRadius: "4px", bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 600 }}>
          {getCookStatus(cook)}
        </Typography>
        <Box
          component="span"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onCookProfile?.(cook.cookId); }}
          sx={{ fontSize: "0.72rem", color: "#2563EB", fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
        >
          ID: {cook.cookId} →
        </Box>
        {dist !== null && (
          <Typography sx={{ fontSize: "0.7rem", px: 0.75, py: 0.2, borderRadius: "4px", bgcolor: within ? "#dcfce7" : "#f1f5f9", color: within ? "#15803d" : "#64748b", fontWeight: 600 }}>
            {fmtDist(dist)} from house
          </Typography>
        )}
      </Box>
      {cook.ongoingVisitId && (
        <Box sx={{ mb: 0.35 }}>
          <Typography variant="body2" fontSize="0.78rem" color="text.secondary">
            Ongoing · House #{cook.ongoingHouseId} · {cook.ongoingVisitTime}
          </Typography>
          <Box onClick={() => onNavigate(`/OneMealService/${cook.ongoingVisitId}/show`)}
            sx={{ fontSize: "0.7rem", color: "#2563EB", fontWeight: 600, cursor: "pointer", width: "fit-content", "&:hover": { textDecoration: "underline" } }}>
            OMS #{cook.ongoingVisitId} →
          </Box>
        </Box>
      )}
      {cook.nextVisitId && (
        <Box sx={{ mb: 0.35 }}>
          <Typography variant="body2" fontSize="0.78rem" color="text.secondary">
            Next · House #{cook.nextHouseId} · {cook.nextVisitTime}
          </Typography>
          <Box onClick={() => onNavigate(`/OneMealService/${cook.nextVisitId}/show`)}
            sx={{ fontSize: "0.7rem", color: "#2563EB", fontWeight: 600, cursor: "pointer", width: "fit-content", "&:hover": { textDecoration: "underline" } }}>
            OMS #{cook.nextVisitId} →
          </Box>
        </Box>
      )}
      {!cook.ongoingVisitId && !cook.nextVisitId && (
        <Typography variant="caption" color="text.secondary">No upcoming visits</Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem", display: "block", mt: 0.5 }}>
        Updated: {cook.lastUpdated}
      </Typography>
      {onAssign && (
        <Button
          size="small"
          variant="contained"
          fullWidth
          onClick={onAssign}
          sx={{ mt: 0.75, fontSize: "0.72rem", py: 0.4, textTransform: "none", bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}
        >
          Assign this Cook
        </Button>
      )}
    </Box>
  );
};

function HouseLocation() {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY as string,
  });

  const navigate = useNavigate();
  const houseService = useMemo(() => new HouseLocationService(), []);
  const cookService = useMemo(() => new CookLiveLocationService(), []);

  const [houses, setHouses] = useState<HouseLocationData[]>([]);
  const [cooks, setCooks] = useState<CookLocation[]>([]);
  const [serviceStatus, setServiceStatus] = useState<HouseServiceStatus>("PENDING");
  const [selectedDate, setSelectedDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>("ALL");
  const [cityFilter, setCityFilter] = useState<string>("");
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
  const [selectedHouse, setSelectedHouse] = useState<HouseLocationData | null>(null);
  const [hoveredHouse, setHoveredHouse] = useState<HouseLocationData | null>(null);
  const [hoveredCook, setHoveredCook] = useState<CookLocation | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [rawSampleForDebug, setRawSampleForDebug] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningHouse, setAssigningHouse] = useState<HouseLocationData | null>(null);
  const [assigningCook, setAssigningCook] = useState<any>(null);
  const [allocateMode, setAllocateMode] = useState(false);
  const [clickedCook, setClickedCook] = useState<CookLocation | null>(null);

  const mapRef = useRef<google.maps.Map | null>(null);
  const hoverHouseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverCookTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedHouseItemRef = useRef<HTMLDivElement | null>(null);

  const _focusHouseRaw = sessionStorage.getItem("houseLocationFocusId");
  const _focusCookRaw = sessionStorage.getItem("houseLocationFocusCookId");
  const _focusAllocateRaw = sessionStorage.getItem("houseLocationAllocateMode");
  const pendingFocusHouseId = useRef<number | null>(_focusHouseRaw ? parseInt(_focusHouseRaw, 10) : null);
  const pendingFocusCookId = useRef<number | null>(_focusCookRaw ? parseInt(_focusCookRaw, 10) : null);
  const pendingAllocateMode = useRef<boolean>(_focusAllocateRaw === "1");

  const savedState = useMemo(() => {
    try { const s = sessionStorage.getItem("houseMapState"); return s ? JSON.parse(s) : null; }
    catch { return null; }
  }, []);
  const defaultCenter = savedState?.center ?? DEFAULT_CENTER;
  const defaultZoom = savedState?.zoom ?? 13;

  const getTooltipTransform = (lat: number, lng: number, pinOffsetPx = 40): string => {
    if (!mapRef.current) return `translate(-50%, calc(-100% - ${pinOffsetPx}px))`;
    const bounds = mapRef.current.getBounds();
    if (!bounds) return `translate(-50%, calc(-100% - ${pinOffsetPx}px))`;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const latRange = ne.lat() - sw.lat();
    const lngRange = ne.lng() - sw.lng();
    const isNearTop = lat > sw.lat() + 0.75 * latRange;
    const isNearLeft = lng < sw.lng() + 0.2 * lngRange;
    const isNearRight = lng > sw.lng() + 0.8 * lngRange;
    const yT = isNearTop ? "20px" : `calc(-100% - ${pinOffsetPx}px)`;
    const xT = isNearLeft ? "0" : isNearRight ? "-100%" : "-50%";
    return `translate(${xT}, ${yT})`;
  };

  const saveMapState = () => {
    if (!mapRef.current) return;
    const c = mapRef.current.getCenter();
    const z = mapRef.current.getZoom();
    if (c && z != null) sessionStorage.setItem("houseMapState", JSON.stringify({ center: { lat: c.lat(), lng: c.lng() }, zoom: z }));
  };

  useEffect(() => {
    let mounted = true;
    let isFirstLoad = true;
    const loadHouses = async () => {
      setLoading(true);
      setRawSampleForDebug(null);
      try {
        const houseRaw = await houseService.getHouseLocations({
          status: serviceStatus,
          date: selectedDate,
          paymentStatus: paymentStatusFilter !== "ALL" ? paymentStatusFilter : undefined,
          time: selectedTime || undefined,
          city: cityFilter.trim() || undefined,
        });
        if (!mounted) return;
        if (houseRaw.length > 0) setRawSampleForDebug(JSON.stringify(houseRaw[0], null, 2));
        const h = houseRaw.map(mapHouseItem).filter((x): x is HouseLocationData => x !== null);
        console.log(`[HouseLocation] ${houseRaw.length} raw → ${h.length} houses`);
        setHouses(h);
        setLastRefresh(new Date());

        if (pendingFocusHouseId.current !== null) {
          const focusHouse = h.find((x) => x.id === pendingFocusHouseId.current);
          pendingFocusHouseId.current = null;
          sessionStorage.removeItem("houseLocationFocusId");
          const shouldAllocate = pendingAllocateMode.current;
          pendingAllocateMode.current = false;
          sessionStorage.removeItem("houseLocationAllocateMode");
          if (focusHouse) {
            setSelectedHouse(focusHouse);
            if (shouldAllocate) setAllocateMode(true);
            // map position already restored from savedState; no pan/zoom override needed
          }
        } else if (isFirstLoad && h.length > 0 && mapRef.current && window.google?.maps) {
          isFirstLoad = false;
          const b = new window.google.maps.LatLngBounds();
          h.forEach((x) => b.extend({ lat: x.lat, lng: x.lng }));
          mapRef.current.fitBounds(b);
          setTimeout(() => {
            if (mapRef.current) {
              const z = mapRef.current.getZoom();
              if (z != null && z > 12) mapRef.current.setZoom(12);
            }
          }, 150);
        }
      } catch (e) { console.error("[HouseLocation] house fetch error:", e); }
      finally { if (mounted) setLoading(false); }
    };
    loadHouses();
    const id = setInterval(loadHouses, 30000);
    return () => { mounted = false; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [houseService, serviceStatus, selectedDate, selectedTime, paymentStatusFilter, cityFilter, refreshKey]);

  useEffect(() => {
    let mounted = true;
    const loadCooks = async () => {
      try {
        const cookRaw = await cookService.getRecentCookLocations();
        if (!mounted) return;
        const c = cookRaw.map(mapCookItem).filter((x): x is CookLocation => x !== null);
        console.log(`[HouseLocation] ${c.length} cooks`);
        setCooks(c);

        if (pendingFocusCookId.current !== null) {
          const focusCook = c.find((x) => x.cookId === pendingFocusCookId.current);
          pendingFocusCookId.current = null;
          sessionStorage.removeItem("houseLocationFocusCookId");
          if (focusCook && mapRef.current) {
            mapRef.current.panTo({ lat: focusCook.lat, lng: focusCook.lng });
            mapRef.current.setZoom(16);
            setClickedCook(focusCook);
          }
        }
      } catch (e) { console.error("[HouseLocation] cook fetch error:", e); }
    };
    loadCooks();
    const id = setInterval(loadCooks, 10000);
    return () => { mounted = false; clearInterval(id); };
  }, [cookService]);

  useEffect(() => {
    cityService.fetchAllCities(100, 0, { isCurrentlyServing: "YES" }, {}).then((res) => {
      setCities(res?.data ?? []);
    });
  }, []);

  // Scroll the selected house into view in the sidebar whenever it changes
  useEffect(() => {
    if (selectedHouse && selectedHouseItemRef.current) {
      selectedHouseItemRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedHouse]);

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    if (houses.length > 0 && window.google?.maps) {
      const b = new window.google.maps.LatLngBounds();
      houses.forEach((h) => b.extend({ lat: h.lat, lng: h.lng }));
      map.fitBounds(b);
    }
  };

  const openHouseHover = (h: HouseLocationData) => {
    if (hoverHouseTimer.current) clearTimeout(hoverHouseTimer.current);
    setHoveredHouse(h);
  };
  const closeHouseHover = () => {
    hoverHouseTimer.current = setTimeout(() => setHoveredHouse(null), 200);
  };
  const openCookHover = (c: CookLocation) => {
    if (hoverCookTimer.current) clearTimeout(hoverCookTimer.current);
    setHoveredCook(c);
  };
  const closeCookHover = () => {
    hoverCookTimer.current = setTimeout(() => setHoveredCook(null), 200);
  };

  const handleFocusHouse = (house: HouseLocationData) => {
    setSelectedHouse(house);
    setAllocateMode(false);
    setClickedCook(null);
    setSearchQuery("");
    if (mapRef.current) {
      mapRef.current.panTo({ lat: house.lat, lng: house.lng });
      mapRef.current.setZoom(17);
    }
  };

  const handleAllocateCook = (house: HouseLocationData) => {
    setSelectedHouse(house);
    setAllocateMode(true);
    setClickedCook(null);
    setSearchQuery("");
    if (mapRef.current && window.google?.maps) {
      const latOff = 0.055;
      const lngOff = 0.058;
      mapRef.current.fitBounds({
        north: house.lat + latOff,
        south: house.lat - latOff,
        east: house.lng + lngOff,
        west: house.lng - lngOff,
      });
    }
  };

  const handleCookAssign = (cook: CookLocation, house: HouseLocationData) => {
    setAssigningHouse(house);
    setAssigningCook({ id: cook.cookId, fname: cook.name, lname: "" });
    setAssignDialogOpen(true);
  };

  const freeCooksWithDistance = useMemo((): CookWithDistance[] => {
    if (!selectedHouse) return [];
    return cooks
      .filter(isCookFree)
      .map((c) => {
        const d = haversineKm(selectedHouse.lat, selectedHouse.lng, c.lat, c.lng);
        return { ...c, distanceKm: d, withinRadius: d <= RADIUS_KM };
      })
      .sort((a, b) => a.distanceKm - b.distanceKm);
  }, [cooks, selectedHouse]);

  const countFreeCooksNearby = (h: HouseLocationData) =>
    cooks.filter((c) => isCookFree(c) && haversineKm(h.lat, h.lng, c.lat, c.lng) <= RADIUS_KM).length;

  const sidebarCooks = useMemo(() => {
    if (!searchQuery.trim()) return freeCooksWithDistance;
    const q = searchQuery.toLowerCase();
    return freeCooksWithDistance.filter((c) => c.name.toLowerCase().includes(q));
  }, [freeCooksWithDistance, searchQuery]);

  const sidebarHouses = useMemo(() => {
    let filtered = houses;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((h) =>
        String(h.houseId).includes(q) ||
        h.customerName?.toLowerCase().includes(q) ||
        h.city?.toLowerCase().includes(q) ||
        h.area?.toLowerCase().includes(q)
      );
    }
    return [...filtered].sort(
      (a, b) => parseTimeToMinutes(a.scheduledTime) - parseTimeToMinutes(b.scheduledTime)
    );
  }, [houses, searchQuery]);

  const cooksWithOffsets = useMemo(() => {
    if (!selectedHouse || !allocateMode) return [];
    return withOffsets(cooks.filter(isCookFree));
  }, [cooks, selectedHouse, allocateMode]);

  if (!isLoaded) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="70vh">
        <Typography fontWeight={600}>Loading house locations...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      {/* Header filters */}
      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", borderBottom: "1px solid #e2e8f0" }}>
        <Typography variant="h6" fontWeight={700}>House Location</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          {/* Service Status */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="house-status-filter">Service Status</InputLabel>
            <Select
              labelId="house-status-filter"
              value={serviceStatus}
              label="Service Status"
              onChange={(e: SelectChangeEvent<string>) => {
                setSelectedHouse(null);
                setHouses([]);
                setServiceStatus(e.target.value as HouseServiceStatus);
              }}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Payment Status */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="payment-filter">Payment Status</InputLabel>
            <Select
              labelId="payment-filter"
              value={paymentStatusFilter}
              label="Payment Status"
              onChange={(e: SelectChangeEvent<string>) => {
                setSelectedHouse(null);
                setPaymentStatusFilter(e.target.value);
              }}
            >
              <MenuItem value="ALL">All</MenuItem>
              {Object.values(PaymentStatusEnum).map((v) => (
                <MenuItem key={v} value={v}>{v.replace(/_/g, " ")}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date */}
          <TextField
            size="small"
            type="date"
            label="Date"
            value={selectedDate}
            onChange={(e) => {
              setSelectedHouse(null);
              setSelectedDate(e.target.value);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          {/* Time */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="time-filter">Time</InputLabel>
            <Select
              labelId="time-filter"
              value={selectedTime ?? ""}
              label="Time"
              onChange={(e: SelectChangeEvent<string>) => {
                setSelectedHouse(null);
                setSelectedTime(e.target.value || null);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {Object.values(TimeEnum).map((v) => (
                <MenuItem key={v} value={v}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* City */}
          <Autocomplete
            size="small"
            options={cities}
            getOptionLabel={(o) => o.name}
            value={cities.find((c) => c.name === cityFilter) ?? null}
            onChange={(_, val) => {
              setSelectedHouse(null);
              setCityFilter(val?.name ?? "");
            }}
            sx={{ minWidth: 150 }}
            renderInput={(params) => <TextField {...params} label="City" />}
          />

          {selectedHouse && allocateMode && (
            <Chip
              label={`${RADIUS_KM}km radius · House #${selectedHouse.houseId}`}
              onDelete={() => { setSelectedHouse(null); setAllocateMode(false); setClickedCook(null); }}
              size="small"
              sx={{ bgcolor: "#e0e7ff", color: "#4338ca", fontWeight: 600 }}
            />
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", height: "calc(100vh - 140px)", minHeight: 400 }}>
        {/* Map area */}
        <Box sx={{ flex: 1, position: "relative" }}>
          {loading && houses.length === 0 && (
            <Box sx={{ position: "absolute", inset: 0, zIndex: 2000, bgcolor: "rgba(255,255,255,0.75)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box sx={{ bgcolor: "#ffffff", px: 3, py: 1.5, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }}>
                <Typography fontWeight={600} fontSize="0.9rem">Loading {serviceStatus.replace(/_/g, " ")} houses...</Typography>
              </Box>
            </Box>
          )}

          {houses.length === 0 && rawSampleForDebug && !loading && (
            <Box sx={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 2000, bgcolor: "#fff7ed", border: "1px solid #f97316", borderRadius: 2, px: 2, py: 1, maxWidth: 480, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }}>
              <Typography variant="caption" fontWeight={700} color="#ea580c" display="block">
                API returned data but coordinates could not be extracted.
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", fontSize: "0.65rem", color: "text.secondary" }}>
                {rawSampleForDebug}
              </Typography>
            </Box>
          )}

          {!loading && houses.length === 0 && (
            <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 2000, bgcolor: "rgba(255,255,255,0.93)", px: 3, py: 2, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", textAlign: "center" }}>
              <Typography fontWeight={700} fontSize="0.95rem" color="text.secondary">No houses found</Typography>
              <Typography fontSize="0.8rem" color="text.disabled" mt={0.5}>No {serviceStatus.replace(/_/g, " ")} records available</Typography>
            </Box>
          )}

          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={defaultCenter}
            zoom={defaultZoom}
            onLoad={handleMapLoad}
            onZoomChanged={() => { saveMapState(); }}
            onDragEnd={saveMapState}
          >
            {selectedHouse && allocateMode && (
              <Circle
                center={{ lat: selectedHouse.lat, lng: selectedHouse.lng }}
                radius={RADIUS_M}
                options={{ strokeColor: "#6366f1", strokeOpacity: 0.8, strokeWeight: 2, fillColor: "#6366f1", fillOpacity: 0.07, zIndex: 1 }}
              />
            )}

            {cooksWithOffsets.map((cook) => {
              const within = haversineKm(selectedHouse!.lat, selectedHouse!.lng, cook.lat, cook.lng) <= RADIUS_KM;
              return (
                <OverlayView
                  key={`cook-${cook.cookId}`}
                  position={{ lat: cook.lat, lng: cook.lng }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <Box
                    onMouseEnter={() => openCookHover(cook)}
                    onMouseLeave={closeCookHover}
                    onClick={(e) => {
                      e.stopPropagation();
                      setHoveredCook(null);
                      setClickedCook(clickedCook?.cookId === cook.cookId ? null : cook);
                    }}
                    sx={{
                      transform: `translate(calc(-50% + ${cook.offsetX}px), calc(-50% + ${cook.offsetY}px))`,
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      sx={{
                        height: 36, width: 36, borderRadius: "999px",
                        bgcolor: within ? "#dcfce7" : "#ffffff",
                        border: within ? "2px solid #22c55e" : "1.5px solid rgba(0,0,0,0.10)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.22))",
                      }}
                    >
                      <Typography fontSize="1.4rem">{cook.isMoving ? "🛵" : "👨‍🍳"}</Typography>
                    </Box>
                  </Box>
                </OverlayView>
              );
            })}

            {houses.map((house) => {
              if (allocateMode && selectedHouse !== null && selectedHouse.id !== house.id) return null;
              const color = STATUS_COLORS[house.status] ?? "#64748b";
              const isSel = selectedHouse?.id === house.id;
              return (
                <OverlayView
                  key={`house-${house.id}`}
                  position={{ lat: house.lat, lng: house.lng }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <Box
                    onMouseEnter={() => openHouseHover(house)}
                    onMouseLeave={closeHouseHover}
                    onClick={() => {
                      if (selectedHouse?.id === house.id && !allocateMode) {
                        setSelectedHouse(null);
                        setSearchQuery("");
                      } else {
                        handleFocusHouse(house);
                      }
                    }}
                    sx={{ transform: "translate(-50%, -100%)", cursor: "pointer", position: "relative" }}
                  >
                    <HousePin color={color} selected={isSel} />
                  </Box>
                </OverlayView>
              );
            })}

            {hoveredHouse && (
              <OverlayView
                key={`htip-${hoveredHouse.id}`}
                position={{ lat: hoveredHouse.lat, lng: hoveredHouse.lng }}
                mapPaneName={OverlayView.FLOAT_PANE}
              >
                <HouseTooltip
                  house={hoveredHouse}
                  freeCooksNearby={countFreeCooksNearby(hoveredHouse)}
                  transform={getTooltipTransform(hoveredHouse.lat, hoveredHouse.lng, 46)}
                  onEnter={() => openHouseHover(hoveredHouse)}
                  onLeave={closeHouseHover}
                  onNavigate={(id) => {
                    saveMapState();
                    if (selectedHouse) sessionStorage.setItem("houseLocationFocusId", String(selectedHouse.id));
                    navigate(`/OneMealService/${id}/show`);
                  }}
                />
              </OverlayView>
            )}

            {hoveredCook && (
              <OverlayView
                key={`ctip-${hoveredCook.cookId}`}
                position={{ lat: hoveredCook.lat, lng: hoveredCook.lng }}
                mapPaneName={OverlayView.FLOAT_PANE}
              >
                <CookTooltip
                  cook={hoveredCook}
                  selectedHouse={selectedHouse}
                  transform={getTooltipTransform(hoveredCook.lat, hoveredCook.lng, 22)}
                  onEnter={() => openCookHover(hoveredCook)}
                  onLeave={closeCookHover}
                  onNavigate={(path) => {
                    saveMapState();
                    if (selectedHouse) sessionStorage.setItem("houseLocationFocusId", String(selectedHouse.id));
                    navigate(path);
                  }}
                  onCookProfile={(cookId) => {
                    saveMapState();
                    if (selectedHouse) sessionStorage.setItem("houseLocationFocusId", String(selectedHouse.id));
                    sessionStorage.setItem("houseLocationFocusCookId", String(cookId));
                    sessionStorage.setItem("houseLocationAllocateMode", allocateMode ? "1" : "0");
                    navigate(`/Cooks/${cookId}/show`);
                  }}
                />
              </OverlayView>
            )}

            {clickedCook && (
              <OverlayView
                key={`cclicked-${clickedCook.cookId}`}
                position={{ lat: clickedCook.lat, lng: clickedCook.lng }}
                mapPaneName={OverlayView.FLOAT_PANE}
              >
                <CookTooltip
                  cook={clickedCook}
                  selectedHouse={selectedHouse}
                  transform={getTooltipTransform(clickedCook.lat, clickedCook.lng, 22)}
                  onEnter={() => {}}
                  onLeave={() => {}}
                  onNavigate={(path) => {
                    saveMapState();
                    if (selectedHouse) sessionStorage.setItem("houseLocationFocusId", String(selectedHouse.id));
                    navigate(path);
                  }}
                  onCookProfile={(cookId) => {
                    saveMapState();
                    if (selectedHouse) sessionStorage.setItem("houseLocationFocusId", String(selectedHouse.id));
                    sessionStorage.setItem("houseLocationFocusCookId", String(cookId));
                    sessionStorage.setItem("houseLocationAllocateMode", allocateMode ? "1" : "0");
                    navigate(`/Cooks/${cookId}/show`);
                  }}
                  onAssign={() => {
                    if (selectedHouse) handleCookAssign(clickedCook, selectedHouse);
                    setClickedCook(null);
                  }}
                  onClose={() => setClickedCook(null)}
                />
              </OverlayView>
            )}
          </GoogleMap>
        </Box>

        {/* Sidebar */}
        <Box sx={{ width: SIDEBAR_WIDTH, bgcolor: "#ffffff", borderLeft: "1px solid #e2e8f0", display: "flex", flexDirection: "column", overflow: "hidden" }}>

          <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="h6" fontWeight={700}>
                {selectedHouse && allocateMode ? `Free Cooks Near #${selectedHouse.houseId}` : "Houses"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedHouse && allocateMode
                  ? `${sidebarCooks.filter((c) => c.withinRadius).length} within ${RADIUS_KM}km`
                  : `${sidebarHouses.length} of ${houses.length}`}
              </Typography>
            </Box>
            {selectedHouse && allocateMode && (
              <Box
                onClick={() => { setAllocateMode(false); setClickedCook(null); setSearchQuery(""); }}
                sx={{ fontSize: "0.75rem", color: "#4338ca", fontWeight: 600, cursor: "pointer", mb: 0.75, width: "fit-content", "&:hover": { textDecoration: "underline" } }}
              >
                ← Back to house list
              </Box>
            )}
            <Box sx={{ position: "relative", mt: 0.5 }}>
              <input
                type="text"
                placeholder={selectedHouse ? "Search cooks..." : "Search houses..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: "100%", padding: "8px 12px 8px 32px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px", outline: "none", boxSizing: "border-box" }}
              />
              <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "13px" }}>🔍</span>
            </Box>
          </Box>


          <Box sx={{ flex: 1, overflowY: "auto", p: 1.25 }}>
            {selectedHouse && allocateMode ? (
              sidebarCooks.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4, color: "#94a3b8" }}>
                  <Typography variant="body2">No free cooks found nearby</Typography>
                  <Typography variant="caption" display="block" mt={0.5}>Cooks with visits within 2 hours are excluded</Typography>
                </Box>
              ) : (
                sidebarCooks.map((cook) => {
                  const isWithin = cook.withinRadius;
                  const statusStyle = getCookStatusStyle(cook);
                  const isClicked = clickedCook?.cookId === cook.cookId;
                  return (
                    <Box
                      key={cook.cookId}
                      onClick={() => setClickedCook(isClicked ? null : cook)}
                      sx={{
                        display: "flex", alignItems: "flex-start", gap: 1.25,
                        p: 1.25, mb: 0.75, borderRadius: "10px", cursor: "pointer",
                        backgroundColor: isClicked
                          ? (isWithin ? "#bbf7d0" : "#e2e8f0")
                          : hoveredCook?.cookId === cook.cookId
                            ? (isWithin ? "#dcfce7" : "#f1f5f9")
                            : (isWithin ? "#f0fdf4" : "#ffffff"),
                        border: isClicked ? "1.5px solid #6366f1" : isWithin ? "1px solid #86efac" : "1px solid #e2e8f0",
                        transition: "all 0.15s ease",
                        "&:hover": { backgroundColor: isWithin ? "#dcfce7" : "#f1f5f9", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                      }}
                    >
                      <Box sx={{ width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: isWithin ? "#dcfce7" : "#f1f5f9", border: isWithin ? "2px solid #22c55e" : "2px solid transparent", flexShrink: 0, mt: 0.25 }}>
                        <Typography fontSize="1.1rem">{cook.isMoving ? "🛵" : "👨‍🍳"}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={600} fontSize="0.88rem" noWrap>{cook.name}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: 0.2 }}>
                          <Typography sx={{ fontSize: "0.68rem", px: 0.75, py: 0.15, borderRadius: "4px", bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 600, whiteSpace: "nowrap" }}>
                            {getCookStatus(cook)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>ID: {cook.cookId}</Typography>
                          <Typography sx={{ fontSize: "0.68rem", px: 0.75, py: 0.15, borderRadius: "4px", bgcolor: isWithin ? "#dcfce7" : "#f1f5f9", color: isWithin ? "#15803d" : "#64748b", fontWeight: 600, whiteSpace: "nowrap" }}>
                            {fmtDist(cook.distanceKm)}
                          </Typography>
                        </Box>
                        {cook.nextVisitId && (
                          <Box
                            onClick={(e) => { e.stopPropagation(); saveMapState(); if (selectedHouse) sessionStorage.setItem("houseLocationFocusId", String(selectedHouse.id)); navigate(`/OneMealService/${cook.nextVisitId}/show`); }}
                            sx={{ fontSize: "0.68rem", color: "#2563EB", fontWeight: 600, cursor: "pointer", mt: 0.3, width: "fit-content", "&:hover": { textDecoration: "underline" } }}
                          >
                            Next #{cook.nextVisitId} · {cook.nextVisitTime}
                          </Box>
                        )}
                        {isClicked && (
                          <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              if (selectedHouse) handleCookAssign(cook, selectedHouse);
                              setClickedCook(null);
                            }}
                            sx={{ mt: 0.75, fontSize: "0.7rem", py: 0.3, textTransform: "none", bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}
                          >
                            Assign this Cook
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })
              )
            ) : (
              sidebarHouses.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4, color: "#94a3b8" }}>
                  <Typography variant="body2">No houses found</Typography>
                </Box>
              ) : (
                sidebarHouses.map((house) => {
                  const color = STATUS_COLORS[house.status] ?? "#64748b";
                  const isSelected = selectedHouse?.id === house.id;
                  return (
                    <Box
                      key={house.id}
                      ref={isSelected ? selectedHouseItemRef : null}
                      onClick={() => handleFocusHouse(house)}
                      sx={{
                        p: 1.25, mb: 0.75, borderRadius: "10px", cursor: "pointer",
                        bgcolor: isSelected ? "#f0f4ff" : "#ffffff",
                        border: isSelected ? "1px solid #a5b4fc" : "1px solid #e2e8f0",
                        transition: "all 0.15s ease",
                        "&:hover": { bgcolor: "#f8fafc", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.4 }}>
                        <Typography fontWeight={700} fontSize="0.88rem">House #{house.houseId}</Typography>
                        <Typography
                          variant="caption" fontWeight={600}
                          sx={{ px: 0.75, py: 0.15, borderRadius: "4px", bgcolor: color + "22", color }}
                        >
                          {STATUS_LABEL[house.status]}
                        </Typography>
                      </Box>
                      {/* Time — prominent */}
                      {house.scheduledTime && (
                        <Box sx={{ display: "inline-flex", alignItems: "center", mb: 0.4, px: 0.75, py: 0.15, borderRadius: "4px", bgcolor: "#ede9fe", color: "#7c3aed" }}>
                          <Typography variant="caption" fontWeight={700} fontSize="0.75rem">
                            {house.scheduledTime}
                          </Typography>
                        </Box>
                      )}
                      {(house.area || house.city) && (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {[house.area, house.city].filter(Boolean).join(", ")}
                        </Typography>
                      )}
                      {house.customerName && (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {house.customerName}
                        </Typography>
                      )}
                      {house.status === "PENDING" && (
                        <Box sx={{ mt: 0.75 }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={(e) => { e.stopPropagation(); handleAllocateCook(house); }}
                            sx={{ fontSize: "0.7rem", py: 0.25, px: 1.25, textTransform: "none", bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}
                          >
                            Allocate Cook
                          </Button>
                        </Box>
                      )}
                    </Box>
                  );
                })
              )
            )}
          </Box>

          <Box sx={{ px: 1.5, py: 0.75, borderTop: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {loading && <Chip label="Loading..." size="small" sx={{ bgcolor: "#e2e8f0", height: "20px" }} />}
              {lastRefresh && <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem" }}>Updated: {lastRefresh.toLocaleTimeString()}</Typography>}
              <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem" }}>
                {houses.length} houses · {cooks.filter(isCookFree).length} free cooks
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Assign cook dialog */}
      {assignDialogOpen && assigningHouse && (
        <AssignCookOnOneMealDialog
          open={assignDialogOpen}
          setOpen={setAssignDialogOpen}
          data={{ id: assigningHouse.id }}
          id={String(assigningHouse.id)}
          initialCook={assigningCook}
          onSuccess={() => {
            setAssignDialogOpen(false);
            setAssigningHouse(null);
            setAssigningCook(null);
            setSelectedHouse(null);
            setAllocateMode(false);
            setClickedCook(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </Box>
  );
}

export default HouseLocation;
